// @ts-check

/**
 * @file Renders task assigned user create/edit form.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import {useLocalSearchParams, useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import buildRoutePath from "../../shared/build-route-path"
import Button from "../../components/button"
import Card from "../../components/card"
import errorMessage from "../../shared/error-message"
import TasksSelect from "../../components/tasks/select"
import {TaskAssignedUser} from "../../frontend-models/task-assigned-user"
import Text from "@kaspernj/api-maker/build/utils/text"
import {optionalRouteParam} from "../../shared/route-params"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import UsersSelect from "../../components/users/select"
import {useScreenLoading} from "../../components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} TaskAssignedUsersEditScreenProps */

/**
 * @typedef {object} TaskAssignedUsersEditScreenState
 * @property {boolean} loading Whether assignment data is loading.
 * @property {boolean} saving Whether form submission is running.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 * @property {TaskAssignedUser | null} taskAssignedUser Loaded assignment being edited.
 */

/**
 * Task assigned user edit screen.
 * @augments {ShapeComponent<TaskAssignedUsersEditScreenProps, TaskAssignedUsersEditScreenState>}
 */
class TaskAssignedUsersEditScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {string | null} */
  taskAssignedUserID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  /** @type {TaskAssignedUsersEditScreenState} */
  state = {
    loading: true,
    saving: false,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    taskAssignedUser: /** @type {TaskAssignedUser | null} */ null
  }

  /** Initializes route params and assignment data loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const taskAssignedUserID = optionalRouteParam(params, "id", "Task assigned user ID")
    this.params = params
    this.router = router
    this.taskAssignedUserID = taskAssignedUserID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const taskAssignedUser = taskAssignedUserID
            ? await TaskAssignedUser.find(taskAssignedUserID)
            : null
          if (cancelled) return
          this.s.loading = false
          this.s.taskAssignedUser = taskAssignedUser
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [taskAssignedUserID])
  }

  /**
   * Renders the assignment form.
   * @returns {ReactNode} Task assigned user form content.
   */
  render() {
    const {loading, saving, status, taskAssignedUser} = this.s
    const {taskAssignedUserID} = this.tt
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="taskAssignedUserEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="taskAssignedUserEditScreen"
      >
        <Text
          style={styles.screenTitle ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="taskAssignedUserEditScreen/title"
        >
          {taskAssignedUserID ? _("Edit assignment") : _("Assign user")}
        </Text>
        <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
          <Card
            style={styles.formCard ||= {
              gap: 12,
              maxWidth: 520,
              width: "100%"
            }}
            testID="taskAssignedUserEditScreen/form"
          >
            <TasksSelect
              defaultValue={
                taskAssignedUser ? String(taskAssignedUser.taskId() || "") : ""
              }
              label={_("Task")}
              name="task_id"
              testID="taskAssignedUserEditScreen/taskSelect"
            />
            <UsersSelect
              defaultValue={
                taskAssignedUser ? String(taskAssignedUser.userId() || "") : ""
              }
              label={_("User")}
              name="user_id"
              testID="taskAssignedUserEditScreen/userSelect"
            />
            {status &&
              <Text
                style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                  color: status.kind === "success" ? this.tt.colors.successText : this.tt.colors.dangerText,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="taskAssignedUserEditScreen/statusMessage"
              >
                {status.text}
              </Text>
            }
            <Button
              disabled={saving}
              iconName="floppy-disk"
              label={saving ? _("Saving...") : _("Save")}
              onPress={this.tt.onSubmit}
              testID="taskAssignedUserEditScreen/saveButton"
            />
          </Card>
        </Form>
      </View>
    )
  }

  /**
   * Saves the task assigned user form.
   * @returns {Promise<void>} Resolves when save handling completes.
   */
  onSubmit = async () => {
    const {params, router, taskAssignedUserID} = this.tt
    const {taskAssignedUser} = this.s
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const values = formObject.asObject()
    const attributes = {
      taskId: values.task_id || "",
      userId: values.user_id || ""
    }
    this.s.saving = true
    this.s.status = null
    try {
      if (taskAssignedUser && taskAssignedUserID) {
        await taskAssignedUser.update(attributes)
        router.push(
          buildRoutePath(
            params,
            `/tasks/${taskAssignedUser.taskId()}/assigned-users`
          )
        )
      } else {
        const created = await TaskAssignedUser.create(attributes)
        router.push(
          buildRoutePath(params, `/tasks/${created.taskId()}/assigned-users`)
        )
      }
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<TaskAssignedUsersEditScreenProps>} */
const TaskAssignedUsersEditScreenComponent = memo(shapeComponent(TaskAssignedUsersEditScreen))

export default TaskAssignedUsersEditScreenComponent
